import { FC, ReactNode, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import AuthBackground from '../molecules/auth-background.component';

/** Marca del lienzo oscuro: logo completo (logo.png, lápiz + líneas +
 * "IT DRAFT") dentro de una cápsula clara con resplandor; el filtro de
 * brillo lo hace legible sobre el fondo bosque. El wordmark HTML se
 * esconde por CSS (el lockup ya trae su texto) y quedan el chip CLM y
 * la tagline. Puramente presentacional. */
const BrandLogo = () => (
  <div className="auth__brand">
    <span aria-hidden="true" className="auth__brand-mark">
      <img
        alt=""
        src={`${process.env.PUBLIC_URL}/logo.png`}
        draggable={false}
      />
    </span>
    <span className="auth__brand-text">
      <span className="auth__brand-word">
        <span className="auth__brand-it">IT</span>
        <span className="auth__brand-draft">DRAFT</span>
        <span className="auth__brand-chip">CLM</span>
      </span>
      <span className="auth__brand-tagline">Contract Lifecycle Management</span>
    </span>
  </div>
);

type Feature = {
  key: 'templates' | 'autocomplete' | 'sign';
  icon: ReactNode;
  accent?: boolean;
};

/** Rasgos de la propuesta de valor; los textos viven en `auth.brand.features`. */
const FEATURES: Feature[] = [
  {
    key: 'templates',
    icon: (
      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    ),
  },
  {
    key: 'autocomplete',
    accent: true,
    icon: (
      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    key: 'sign',
    icon: (
      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
];

/**
 * Vistas sin sesión (PRD 5.9): un solo lienzo oscuro con la propuesta de
 * valor y el flujo contractual a la izquierda —sobre un fondo de documentos
 * flotando— y a la derecha la tarjeta blanca elevada con el formulario que
 * toque: ingreso, recuperar o restablecer contraseña.
 */
const AuthLayout: FC = () => {
  const { t, i18n } = useTranslation();

  // Dos ajustes de documento que viven mientras la vista existe:
  // 1) El fondo del body es crema en el resto de la app; sobre este lienzo
  //    oscuro se pintaba un destello blanco en el rebote del scroll.
  // 2) El alto del lienzo se fija al área REALMENTE visible. En un navegador
  //    embebido el dvh puede contar UI que tapa la parte de abajo (el pie
  //    quedaba oculto tras ella); `visualViewport` mide exactamente lo que
  //    se ve y se publica como `--auth-viewport-h` para el CSS.
  useEffect(() => {
    const root = document.documentElement;
    const previousBg = document.body.style.backgroundColor;

    document.body.style.backgroundColor = '#0c1a17';

    // Se toma el MÍNIMO entre las medidas disponibles: algunas vistas web
    // inflan una de ellas (p. ej. innerHeight contando UI que tapa abajo).
    const setVisibleHeight = () => {
      const candidates = [
        window.visualViewport?.height,
        window.innerHeight,
        document.documentElement.clientHeight,
      ].filter((n): n is number => !!n && n > 0);

      if (candidates.length) {
        root.style.setProperty('--auth-viewport-h', `${Math.floor(Math.min(...candidates))}px`);
      }
    };

    setVisibleHeight();
    // Las vistas web embebidas suelen asentar su tamaño real un poco después
    // del montaje, sin disparar `resize`; sin estas re-medidas el pie quedaba
    // clavado a un alto viejo y "comido" por la UI del navegador anfitrión.
    const raf = requestAnimationFrame(setVisibleHeight);
    const late = window.setTimeout(setVisibleHeight, 300);

    window.visualViewport?.addEventListener('resize', setVisibleHeight);
    window.visualViewport?.addEventListener('scroll', setVisibleHeight);
    window.addEventListener('resize', setVisibleHeight);
    window.addEventListener('orientationchange', setVisibleHeight);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(late);
      window.visualViewport?.removeEventListener('resize', setVisibleHeight);
      window.visualViewport?.removeEventListener('scroll', setVisibleHeight);
      window.removeEventListener('resize', setVisibleHeight);
      window.removeEventListener('orientationchange', setVisibleHeight);
      root.style.removeProperty('--auth-viewport-h');
      document.body.style.backgroundColor = previousBg;
    };
  }, []);

  const metricas = [
    { valor: t('auth.brand.metric_docs_value'), etiqueta: t('auth.brand.metric_docs_label') },
    { valor: t('auth.brand.metric_cycle_value'), etiqueta: t('auth.brand.metric_cycle_label') },
    { valor: t('auth.brand.metric_users_value'), etiqueta: t('auth.brand.metric_users_label') },
  ];

  const idiomas = ['es', 'en'];

  return (
    <div className="auth">
      <AuthBackground />

      {/* Resplandores ambientales (decorativos). */}
      <div aria-hidden="true" className="auth__glow auth__glow--a" />
      <div aria-hidden="true" className="auth__glow auth__glow--b" />
      <div aria-hidden="true" className="auth__glow auth__glow--c" />

      {/* Hilo conductor: líneas de flujo con nodos pulsantes (decorativo). */}
      <svg
        aria-hidden="true"
        className="auth__thread"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 1440 900"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="authThreadGradient" x1="0%" x2="100%" y1="0%" y2="100%">
            <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.2" />
            <stop offset="35%" stopColor="#2dd4bf" stopOpacity="0.9" />
            <stop offset="70%" stopColor="#5eead4" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#14b8a6" stopOpacity="0.2" />
          </linearGradient>
        </defs>
        <path
          className="auth__thread-line"
          d="M -50,220 C 250,150 400,320 700,240 C 1000,160 1200,340 1500,260"
          stroke="url(#authThreadGradient)"
          strokeWidth="2.2"
        />
        <path
          className="auth__thread-line auth__thread-line--reverse"
          d="M 0,620 C 350,540 500,750 820,640 C 1140,530 1300,720 1500,600"
          stroke="url(#authThreadGradient)"
          strokeOpacity="0.7"
          strokeWidth="1.8"
        />
        <circle className="auth__thread-node" cx="360" cy="228" fill="#5eead4" r="3.5" />
        <circle className="auth__thread-node" cx="700" cy="240" fill="#2dd4bf" r="3.5" />
        <circle className="auth__thread-node" cx="1080" cy="230" fill="#5eead4" r="3.5" />
      </svg>

      <div className="auth__frame">
        <header className="auth__header">
          <BrandLogo />

          <nav aria-label={t('auth.brand.language')} className="auth__lang" role="group">
            {idiomas.map((lng, i) => (
              <span key={lng}>
                {i > 0 && <i aria-hidden="true">·</i>}
                <button
                  type="button"
                  className={i18n.language?.startsWith(lng) ? 'is-active' : ''}
                  onClick={() => i18n.changeLanguage(lng)}
                >
                  {lng.toUpperCase()}
                </button>
              </span>
            ))}
          </nav>
        </header>

        <main className="auth__main">
          <section className="auth__panel">
            <p className="auth__badge">
              <span aria-hidden="true" className="auth__badge-dot" />
              {t('auth.brand.badge')}
            </p>

            <div className="auth__pitch">
              <h1>
                {t('auth.brand.pitch_lead')} <em>{t('auth.brand.pitch_accent')}</em>
              </h1>
              <p>{t('auth.brand.subtitle')}</p>
            </div>

            <ul className="auth__features">
              {FEATURES.map((feature) => (
                <li className="auth__feature" key={feature.key}>
                  <div className="auth__feature-head">
                    <span className={`auth__feature-icon${feature.accent ? ' auth__feature-icon--accent' : ''}`}>
                      {feature.icon}
                    </span>
                    <span className="auth__feature-chip">
                      {t(`auth.brand.features.${feature.key}.badge`)}
                    </span>
                  </div>
                  <div className="auth__feature-title">
                    {t(`auth.brand.features.${feature.key}.title`)}
                  </div>
                  <div className="auth__feature-sub">
                    {t(`auth.brand.features.${feature.key}.subtitle`)}
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className="auth__side">
            <Outlet />
          </section>
        </main>

        <footer className="auth__footer">
          <ul className="auth__metrics">
            {metricas.map((m) => (
              <li key={m.etiqueta}>
                <strong>{m.valor}</strong>
                <span>{m.etiqueta}</span>
              </li>
            ))}
          </ul>
        </footer>
      </div>
    </div>
  );
};

export default AuthLayout;
